import React, { useMemo, useEffect, useState, useRef } from "react"
import FeaturesScroll from "./FeaturesScroll"
import Button from "aldoo-ra/Button"
import Text from "aldoo-ra/Text"
import MenuGroup from "aldoo-ra/MenuGroup"
import { useFeatureController } from "./FeatureController"
import CMSData from "aldoo-ra/CMS/cms-data"
import useSitePlatform from "@components/Site/useSitePlatform"

export default function FeaturesAndBenefits() {
  const [platform] = useSitePlatform()
  const loadedPlatforms = useRef(new Set()) // Track loaded platforms

  // Initialize the controller once at the parent level, but disable its data loading
  const controller = useFeatureController({
    //just change the features, don't change the URL
    skipURLSync: true,
  })
  
  // Create a simple state for selected feature instead of relying on controller
  const [localSelectedFeature, setLocalSelectedFeature] = useState(null)
  

  const platformsQuery = CMSData({
    contentType: "AquaMailFeaturePlatform",
  })

  // Current platform ID
  const currentPlatformId = useMemo(() => {
    return platformsQuery.data?.find((p) => p.id === platform)?._id
  }, [platformsQuery.data, platform])

  const {
    data: highlights,
    refresh: loadFeatures,
    loading,
  } = CMSData({
    contentType: "HighlightFeatures",
    match: {
      "content.platform._id": currentPlatformId,
    },
    manual: true,
  })

  useEffect(() => {
    // Only run on client-side to avoid SSR issues
    if (typeof window === 'undefined') return
    if (!currentPlatformId) return
    if (!loadFeatures || typeof loadFeatures !== 'function') return
    // Don't load if already loaded for this platform
    if (loadedPlatforms.current.has(currentPlatformId)) return
    
    loadedPlatforms.current.add(currentPlatformId)
    loadFeatures()
  }, [currentPlatformId])

  // Initialize with first feature when features are loaded
  const features = highlights?.[0]?.features
  useEffect(() => {
    if (!localSelectedFeature && features?.length > 0) {
      setLocalSelectedFeature(features[0])
    }
  }, [localSelectedFeature, features])
  
  // Use local state for selected feature
  const selectedFeature = localSelectedFeature || (features?.[0])
  
  // Create the controller with custom navigation functions
  const customController = useMemo(() => {
    if (!features?.length) return null
    
    return {
      ...controller,
      selectedFeature, // Default to first feature if none selected
      selectFeatureByID: (id) => {
        const feature = features.find((f) => f.id === id)
        if (feature) {
          setLocalSelectedFeature(feature)
        }
      },
      getData: () => features,
      // Custom next/prev functions that work with individual features instead of groups
      selectNextGroup: () => {
        if (selectedFeature && features.length > 0) {
          const currentIndex = features.findIndex((f) => f.id === selectedFeature.id)
          const nextIndex = (currentIndex + 1) % features.length // Loop back to start
          setLocalSelectedFeature(features[nextIndex])
        }
      },
      selectPrevGroup: () => {
        if (selectedFeature && features.length > 0) {
          const currentIndex = features.findIndex((f) => f.id === selectedFeature.id)
          const prevIndex = currentIndex <= 0 ? features.length - 1 : currentIndex - 1 // Loop to end
          setLocalSelectedFeature(features[prevIndex])
        }
      }
    }
  }, [controller, features, selectedFeature])

  
  if (!highlights?.length) return null
  if (!features?.length) return null
  const currentIndex = selectedFeature ? features.findIndex((f) => f.id === selectedFeature.id) : 0

  return (
    <div>
      <Text
        id="features_benefits_title"
        component="h2"
        variant="h1"
        className="text-center mb-2"
      />
      <div>
        <FeaturesScroll
          overwriteFeatures={features}
          controller={customController}
          cardDistance={220}
          scrollHeight={{
            default: 520,
            xs: 520,
            sm: 540,
            md: 480,
            lg: 500,
          }}
        />
      </div>

      {/* try free button */}
      <div className="w-full flex justify-center">
        <MenuGroup
          name="TryAppFree"
          renderAs="hlist"
          itemsAsLinks={false}
          menuItemRender={(item) => {
            const url =
              (item?.link[0]?.parameters &&
                item?.link[0]?.parameters[platform]) ||
              item?.link[0]?.url

            return (
              <Button variant="primary_l">
                <a href={url} target="_blank">
                  {item?.label}
                </a>
              </Button>
            )
          }}
        />
      </div>
    </div>
  )
}
